"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWhatsAppUrl } from "../lib/whatsapp";

const SERVICE_OPTIONS = [
  "Calentador",
  "Estufa",
  "Horno",
  "Plomería",
  "Gas natural / GLP",
  "Reparaciones locativas",
  "Otro",
] as const;

const URGENCY_OPTIONS = [
  "Hoy mismo",
  "Esta semana",
  "Sin afán",
] as const;

const formSchema = z.object({
  name: z
    .string()
    .min(2, "Escribe tu nombre completo")
    .max(80, "Demasiado largo"),
  phone: z
    .string()
    .regex(
      /^(\+?\d[\d\s\-().]{6,})$/,
      "Teléfono inválido — usa solo números, mínimo 7 dígitos"
    ),
  service: z.enum(SERVICE_OPTIONS, {
    errorMap: () => ({ message: "Elige un servicio" }),
  }),
  address: z.string().max(160).optional().or(z.literal("")),
  urgency: z.enum(URGENCY_OPTIONS).optional(),
  message: z.string().max(500).optional().or(z.literal("")),
});

type FormValues = z.infer<typeof formSchema>;

export default function ContactForm() {
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      phone: "",
      service: "Calentador",
      address: "",
      urgency: "Hoy mismo",
      message: "",
    },
  });

  const onSubmit = (data: FormValues) => {
    setSubmitting(true);
    const url = buildWhatsAppUrl({
      name: data.name,
      phone: data.phone,
      service: data.service,
      address: data.address || undefined,
      urgency: data.urgency,
      message: data.message || undefined,
    });
    // Abrir WhatsApp en nueva pestaña
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    setTimeout(() => setSubmitting(false), 800);
  };

  const fieldClass =
    "w-full rounded-xl border border-border bg-background px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground/70 transition focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-200";
  const labelClass =
    "block text-sm font-semibold text-brand-900 mb-1.5";
  const errorClass = "mt-1.5 text-xs font-semibold text-destructive";

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5 text-left"
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className={labelClass}>
            Nombre completo
          </label>
          <input
            id="name"
            {...register("name")}
            className={fieldClass}
            placeholder="Ana Pérez"
            autoComplete="name"
          />
          {errors.name && <p className={errorClass}>{errors.name.message}</p>}
        </div>

        <div>
          <label htmlFor="phone" className={labelClass}>
            Teléfono / WhatsApp
          </label>
          <input
            id="phone"
            {...register("phone")}
            className={fieldClass}
            placeholder="3001234567"
            inputMode="tel"
            autoComplete="tel"
          />
          {errors.phone && <p className={errorClass}>{errors.phone.message}</p>}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="service" className={labelClass}>
            Servicio
          </label>
          <select
            id="service"
            {...register("service")}
            className={`${fieldClass} appearance-none bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-10`}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231e293b' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
            }}
          >
            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          {errors.service && (
            <p className={errorClass}>{errors.service.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="urgency" className={labelClass}>
            Urgencia
          </label>
          <select
            id="urgency"
            {...register("urgency")}
            className={`${fieldClass} appearance-none bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-10`}
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231e293b' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
            }}
          >
            {URGENCY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="address" className={labelClass}>
          Dirección o zona <span className="text-muted-foreground font-normal">(opcional)</span>
        </label>
        <input
          id="address"
          {...register("address")}
          className={fieldClass}
          placeholder="Ej: Suba, Chapinero, Cra 13 # 60-15"
          autoComplete="street-address"
        />
      </div>

      <div>
        <label htmlFor="message" className={labelClass}>
          Cuéntanos qué pasa <span className="text-muted-foreground font-normal">(opcional)</span>
        </label>
        <textarea
          id="message"
          {...register("message")}
          rows={3}
          className={`${fieldClass} resize-none`}
          placeholder="El calentador no enciende desde anoche…"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="h-14 w-full gap-3 rounded-full bg-flame-600 text-base font-bold text-white shadow-md hover:bg-flame-700 disabled:opacity-70"
      >
        {submitting ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <MessageCircle className="size-5" />
        )}
        Enviar por WhatsApp
      </Button>
      <p className="text-center text-xs text-muted-foreground">
        Al enviar, abriremos WhatsApp con tu mensaje listo para confirmar.
      </p>
    </form>
  );
}
